import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { fontSizes, fontFamilies, FontSizeType, FontFamilyType } from '../config/fonts';

interface FontContextType {
  fontSize: FontSizeType;
  setFontSize: (size: FontSizeType) => void;
  fontFamily: FontFamilyType;
  setFontFamily: (family: FontFamilyType) => void;
  fontSizes: typeof fontSizes;
  fontFamilies: typeof fontFamilies;
}

const FontContext = createContext<FontContextType | null>(null);

interface FontProviderProps {
  children: ReactNode;
}

export function FontProvider({ children }: FontProviderProps) {
  const [fontSize, setFontSize] = useState<FontSizeType>(() => {
    const saved = localStorage.getItem('fontSize');
    return (saved as FontSizeType) || 'medium';
  });

  const [fontFamily, setFontFamily] = useState<FontFamilyType>(() => {
    const saved = localStorage.getItem('fontFamily');
    return (saved as FontFamilyType) || 'system';
  });

  useEffect(() => {
    localStorage.setItem('fontSize', fontSize);
    
    const sizeConfig = fontSizes[fontSize];
    Object.keys(sizeConfig).forEach((key) => {
      document.documentElement.style.setProperty(
        `--font-size-${key}`, 
        sizeConfig[key as keyof typeof sizeConfig]
      );
    });
  }, [fontSize]);

  useEffect(() => {
    localStorage.setItem('fontFamily', fontFamily);
    
    const familyConfig = fontFamilies[fontFamily];
    document.documentElement.style.setProperty(
      '--font-family', 
      familyConfig.family
    );
  }, [fontFamily]);

  const value: FontContextType = { 
    fontSize, 
    setFontSize, 
    fontFamily, 
    setFontFamily,
    fontSizes,
    fontFamilies
  };

  return (
    <FontContext.Provider value={value}>
      {children}
    </FontContext.Provider>
  );
}

export function useFont(): FontContextType {
  const context = useContext(FontContext);
  if (!context) {
    throw new Error('useFont must be used within FontProvider');
  }
  return context;
}